import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Card, Dialog, Heading, SimpleGrid, Grid, GridItem, Spinner, Flex, HStack } from "@chakra-ui/react";
import { Button } from "components/ui/Button";
import StatCard from "../../../components/utils/StatCard";
import {
  MdOutlinePeople,
  MdOutlineCheckCircle,
  MdOutlineSupervisorAccount,
  MdOutlinePayments,
  MdOutlineSportsEsports,
} from "react-icons/md";
import CardActs from "./components/CardActs";
import MainAct from "./components/MainAct";
import { RecomendacionesPanel } from "./components/RecomendacionesPanel";
import {
  useDashboardActividades,
  useCreateDashboardActividad,
  useDeleteDashboardActividad,
  usePatchDashboardActividad,
} from "features/dashboard/actividades/hooks";
import {
  compareActividadesForDisplay,
  normalizeActividadForCard,
} from "./actividadesList.helpers";

const labelStyle = {
  fontSize: "10px",
  fontWeight: "800",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const Actividades = () => {
  const navigate = useNavigate();
  const q = useDashboardActividades();
  const createAct = useCreateDashboardActividad();
  const deleteAct = useDeleteDashboardActividad();
  const patchAct = usePatchDashboardActividad();
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  const acts = useMemo(() => {
    const rows = Array.isArray(q.data) ? q.data : [];
    return [...rows].sort(compareActividadesForDisplay);
  }, [q.data]);

  const main = useMemo(() => {
    if (!acts.length) return null;
    return normalizeActividadForCard(acts[0]);
  }, [acts]);

  const secondaryActs = useMemo(
    () => acts.filter((a) => a.id !== main?.id),
    [acts, main?.id],
  );

  const secondaryCards = useMemo(
    () => secondaryActs.map((actividad) => normalizeActividadForCard(actividad)),
    [secondaryActs],
  );

  const onDelete = (id) => {
    const actividad = acts.find((a) => a.id === id);
    setDeleteTarget({
      id,
      nombre: actividad?.nombre?.trim() || "esta actividad",
    });
  };
  const closeDeleteDialog = () => setDeleteTarget(null);
  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteAct.mutateAsync(deleteTarget.id);
      closeDeleteDialog();
    } catch (e) {
      console.error(e);
    }
  };
  const onTogglePublish = (id, published) =>
    patchAct.mutate({ actividadId: id, patch: { published: !published } });
  const onAdd = async () => {
    try {
      const created = await createAct.mutateAsync();
      const actividadId = created?.actividadId || created?.id;
      if (actividadId) navigate(`/dashboard/club/actividades/${actividadId}`);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box>

      {/* Page header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap="4"
        mb="6"
      >
        <Box>
          <Text
            fontFamily="display"
            fontWeight="700"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="dashboard.fg.default"
            lineHeight="1.1"
          >
            Actividades
          </Text>
          <Text fontSize="sm" color="dashboard.fg.muted" mt="0.5">
            Gestiona tus actividades y recomendaciones
          </Text>
        </Box>

        <Button
          color="red"
          variant="solid"
          onClick={onAdd}
          disabled={createAct.isPending || q.isLoading}
          alignSelf={{ base: "stretch", md: "auto" }}
          radius="xl" fontSize="lg"
        >
          {createAct.isPending ? <Spinner size="sm" /> : <MdOutlineSportsEsports />}
          Agregar actividad
        </Button>
      </Flex>

      {/* Stats */}
      <SimpleGrid pb="6" columns={[2, 4]} gap="3">
        <StatCard title="Socios Registrados" data="28" icon={MdOutlinePeople} tone="info" />
        <StatCard title="Asistencia Promedio" data="11" icon={MdOutlineCheckCircle} tone="success" />
        <StatCard title="Preceptorias Promedio" data="8" icon={MdOutlineSupervisorAccount} tone="warning" />
        <StatCard title="Presupuesto Restante" data="3783" icon={MdOutlinePayments} prefix="$" tone="danger" />
      </SimpleGrid>

      {q.isError && (
        <pre style={{ whiteSpace: "pre-wrap", color: "crimson" }}>
          {String(q.error?.message || q.error)}
        </pre>
      )}

      {/* Featured + recommendations */}
      <Grid templateColumns={{ base: "1fr", lg: "repeat(8, 1fr)" }} gap="5" pb="6">
        <GridItem colSpan={{ base: 1, lg: 5 }}>
          {q.isLoading ? (
            <Card.Root
              minH="300px"
              rounded="card"
              bg="dashboard.bg.surface"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <Card.Body display="flex" alignItems="center" justifyContent="center">
                <Spinner />
              </Card.Body>
            </Card.Root>
          ) : main ? (
            <MainAct
              id={main.id}
              nombre={main.nombre}
              fecha={main.fecha}
              desc={main.desc}
              horaInicio={main.horaInicio}
              horaFin={main.horaFin}
              foto={main.foto}
              published={main.published}
              isIncomplete={main.isIncomplete}
              onDelete={onDelete}
              onTogglePublish={() => onTogglePublish(main.id, main.published)}
            />
          ) : (
            <Card.Root
              minH="300px"
              rounded="card"
              bg="dashboard.bg.surface"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <Card.Header>
                <Heading fontFamily="display" size="md" color="dashboard.fg.default">
                  Sin actividades
                </Heading>
              </Card.Header>
              <Card.Body color="dashboard.fg.muted" fontSize="sm">
                Crea tu primera actividad con <b>Agregar actividad</b>.
              </Card.Body>
            </Card.Root>
          )}
        </GridItem>

        <GridItem colSpan={{ base: 1, lg: 3 }} display={{ base: "none", md: "block" }}>
          <RecomendacionesPanel />
        </GridItem>
      </Grid>

      {/* Secondary activities — mobile horizontal scroll */}
      <Box
        display={{ base: "block", lg: "none" }}
        overflowX="auto"
        overflowY="hidden"
        pb="2"
        css={{ scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}
      >
        {secondaryCards.length ? (
          <Flex gap="3" minW="max-content" pr="1">
            {secondaryCards.map((item) => (
              <Box
                key={item.id}
                flex={{ base: "0 0 46%", sm: "0 0 42%", md: "0 0 30%" }}
                minW={{ base: "150px", sm: "170px", md: "220px" }}
                maxW={{ base: "200px", sm: "220px", md: "260px" }}
                scrollSnapAlign="start"
              >
                <CardActs
                  id={item.id}
                  nombre={item.nombre}
                  fecha={item.fecha}
                  socios={item.socios}
                  instructores={item.instructores}
                  preceptorias={item.preceptorias}
                  foto={item.foto}
                  published={item.published}
                  isIncomplete={item.isIncomplete}
                  onDelete={onDelete}
                  onTogglePublish={() => onTogglePublish(item.id, item.published)}
                />
              </Box>
            ))}
          </Flex>
        ) : (
          <Text color="dashboard.fg.muted" fontSize="sm">
            No hay actividades adicionales.
          </Text>
        )}
      </Box>

      {/* Secondary activities — desktop grid */}
      {secondaryCards.length > 0 && (
        <Box display={{ base: "none", lg: "block" }}>
          <Text sx={labelStyle} color="dashboard.fg.subtle" mb="3">
            Más actividades
          </Text>
          <SimpleGrid columns={5} gap="4">
            {secondaryCards.map((item) => (
              <CardActs
                key={item.id}
                id={item.id}
                nombre={item.nombre}
                fecha={item.fecha}
                socios={item.socios}
                instructores={item.instructores}
                preceptorias={item.preceptorias}
                foto={item.foto}
                published={item.published}
                isIncomplete={item.isIncomplete}
                onDelete={onDelete}
                onTogglePublish={() => onTogglePublish(item.id, item.published)}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      <Dialog.Root
        open={!!deleteTarget}
        onOpenChange={(e) => {
          if (!e.open && !deleteAct.isPending) closeDeleteDialog();
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="md" bg="dashboard.bg.dialog" color="dashboard.fg.default" borderWidth="1px" borderColor="dashboard.border.default">
            <Dialog.Header>
              <Dialog.Title fontWeight="800">Eliminar actividad</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text color="dashboard.fg.default" fontWeight="700">
                Estas seguro que quieres eliminar esta actividad?
              </Text>
              {deleteTarget?.nombre && deleteTarget.nombre !== "esta actividad" ? (
                <Text color="dashboard.fg.muted" fontSize="sm" mt="2">
                  {deleteTarget.nombre}
                </Text>
              ) : null}
            </Dialog.Body>
            <Dialog.Footer>
              <HStack justify="flex-end" w="full" gap="2">
                <Button variant="ghost" onClick={closeDeleteDialog} disabled={deleteAct.isPending}>
                  Cancelar
                </Button>
                <Button color="red" variant="surface" onClick={confirmDelete} disabled={deleteAct.isPending}>
                  {deleteAct.isPending ? <Spinner size="sm" /> : null}
                  Eliminar
                </Button>
              </HStack>
            </Dialog.Footer>
            <Dialog.CloseTrigger />
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

    </Box>
  );
};

export default Actividades;
